import React, { createContext, useContext } from "react";

// Context for toggle group state
interface ToggleGroupContextValue {
  value: string | string[];
  onChange: (value: string) => void;
  exclusive: boolean;
  size: "sm" | "md" | "lg";
  color: "primary" | "secondary" | "accent";
}

const ToggleGroupContext = createContext<ToggleGroupContextValue | null>(null);

const useToggleGroupContext = () => {
  const context = useContext(ToggleGroupContext);
  if (!context) {
    throw new Error("ToggleButton must be used within a ToggleGroup component");
  }
  return context;
};

export interface ToggleGroupProps {
  /** Currently selected value(s) */
  value: string | string[];
  /** Callback when value changes */
  onChange: (value: string | string[]) => void;
  /** If true, only one button can be selected at a time */
  exclusive?: boolean;
  /** Size of the buttons */
  size?: "sm" | "md" | "lg";
  /** Color theme */
  color?: "primary" | "secondary" | "accent";
  /** Children (ToggleGroupButton components) */
  children: React.ReactNode;
  /** Additional class name */
  className?: string;
  /** Full width */
  fullWidth?: boolean;
  /** Orientation */
  orientation?: "horizontal" | "vertical";
}

export interface ToggleGroupButtonProps {
  /** Value of this button */
  value: string;
  /** Button content */
  children: React.ReactNode;
  /** Disabled state */
  disabled?: boolean;
  /** Additional class name */
  className?: string;
}

/**
 * Arc-style ToggleGroup component
 * 
 * Features:
 * - Exclusive (single) or multiple selection
 * - Multiple sizes
 * - Smooth transitions
 * - Keyboard navigation
 * - Dynamic accent color from workspace theme
 */
const ToggleGroup: React.FC<ToggleGroupProps> & {
  Button: React.FC<ToggleGroupButtonProps>;
} = ({
  value,
  onChange,
  exclusive = true,
  size = "md",
  color = "accent",
  children,
  className = "",
  fullWidth = false,
  orientation = "horizontal",
}) => {
  const handleChange = (buttonValue: string) => {
    if (exclusive) {
      // Single selection mode
      onChange(buttonValue);
    } else {
      // Multiple selection mode
      const currentValues = Array.isArray(value) ? value : [value].filter(Boolean);
      if (currentValues.includes(buttonValue)) {
        onChange(currentValues.filter((v) => v !== buttonValue));
      } else {
        onChange([...currentValues, buttonValue]);
      }
    }
  };

  return (
    <ToggleGroupContext.Provider
      value={{ value, onChange: handleChange, exclusive, size, color }}
    >
      <div
        role="group"
        className={`
          inline-flex
          ${orientation === "vertical" ? "flex-col" : "flex-row"}
          ${fullWidth ? "w-full" : ""}
          bg-surface-secondary
          rounded-arc-md
          p-0.5
          ${className}
        `}
      >
        {children}
      </div>
    </ToggleGroupContext.Provider>
  );
};

/**
 * ToggleGroup Button component
 */
const ToggleGroupButton: React.FC<ToggleGroupButtonProps> = ({
  value,
  children,
  disabled = false,
  className = "",
}) => {
  const { value: groupValue, onChange, size, color } = useToggleGroupContext();

  const isSelected = Array.isArray(groupValue)
    ? groupValue.includes(value)
    : groupValue === value;

  // Size styles
  const sizeStyles = {
    sm: "px-2.5 py-1 text-xs",
    md: "px-3.5 py-1.5 text-sm",
    lg: "px-5 py-2 text-base",
  };

  // Color styles for selected state - all use dynamic accent colors
  const colorStyles = {
    primary: "bg-accent text-content-inverse shadow-arc-accent",
    secondary: "bg-accent-dark text-content-inverse shadow-arc-accent",
    accent: "bg-accent text-content-inverse shadow-arc-accent",
  };

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      disabled={disabled}
      onClick={() => !disabled && onChange(value)}
      className={`
        flex items-center justify-center
        font-medium
        rounded-arc-sm
        transition-all duration-arc-fast ease-arc
        focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1
        ${sizeStyles[size]}
        ${
          isSelected
            ? `${colorStyles[color]}`
            : "text-content-secondary hover:text-content-primary hover:bg-surface-tertiary"
        }
        ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
        ${className}
      `}
    >
      {children}
    </button>
  );
};

// Attach compound component
ToggleGroup.Button = ToggleGroupButton;

export { ToggleGroup, ToggleGroupButton };
export default ToggleGroup;
